import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { OotItems } from '@/ootr/hints/OotItemAutocomplete';
import type { OotItemDto } from '@/ootr/hints/OotItemAutocomplete';
import type { ShopItem } from '../data/entrance-types';

import RupeeIcon from '@/core/assets/oot/classic/Green_Rupee_Artwork.png';

const MAX_SLOTS = 8;

function emptySlots(): ShopItem[] {
  return Array.from({ length: MAX_SLOTS }, () => ({ itemName: null, price: null }));
}

interface ShopEditorModalProps {
  entranceId: string;
  items: ShopItem[];
  onSave: (entranceId: string, items: ShopItem[]) => void;
  onClose: () => void;
}

export function ShopEditorModal({ entranceId, items, onSave, onClose }: ShopEditorModalProps) {
  const [slots, setSlots] = useState<ShopItem[]>(() => {
    const base = emptySlots();
    for (let i = 0; i < items.length && i < MAX_SLOTS; i++) {
      base[i] = { ...items[i] };
    }
    return base;
  });

  const backdropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === backdropRef.current) {
      handleSave();
    }
  };

  const updateSlot = (index: number, patch: Partial<ShopItem>) => {
    setSlots(prev => prev.map((s, i) => i === index ? { ...s, ...patch } : s));
  };

  const handleSave = () => {
    onSave(entranceId, slots);
    onClose();
  };

  const filledCount = slots.filter(s => s.itemName !== null).length;

  return createPortal(
    <div
      ref={backdropRef}
      className="fixed inset-0 bg-black/60 flex items-center justify-center"
      style={{ zIndex: 10000 }}
      onClick={handleBackdropClick}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div
        className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-[420px] max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <h3 className="text-sm font-bold text-gray-200">
            Shop Items ({filledCount}/{MAX_SLOTS})
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-300 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Slots */}
        <div className="p-3 space-y-1.5">
          {slots.map((slot, i) => (
            <ShopSlotRow
              key={i}
              index={i}
              slot={slot}
              onUpdate={(patch) => updateSlot(i, patch)}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-700">
          <button
            onClick={() => setSlots(emptySlots())}
            className="px-3 py-1.5 text-[11px] text-gray-500 hover:text-gray-300 border border-gray-700 rounded transition-colors"
          >
            Clear all
          </button>
          <button
            onClick={handleSave}
            className="px-3 py-1.5 text-[11px] text-gray-900 bg-zelda-gold hover:bg-zelda-gold/80 rounded font-medium transition-colors"
          >
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

interface ShopSlotRowProps {
  index: number;
  slot: ShopItem;
  onUpdate: (patch: Partial<ShopItem>) => void;
}

function ShopSlotRow({ index, slot, onUpdate }: ShopSlotRowProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedItem = slot.itemName ? OotItems.find(i => i.name === slot.itemName) : null;

  useEffect(() => {
    if (!isDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isDropdownOpen]);

  const filteredItems = OotItems.filter(item => {
    if (!search) return true;
    const term = search.toLowerCase();
    return item.name.toLowerCase().includes(term) ||
      item.tags.some(t => t.toLowerCase().includes(term));
  });

  const selectItem = (item: OotItemDto) => {
    onUpdate({ itemName: item.name });
    setIsDropdownOpen(false);
    setSearch('');
  };

  const clearItem = () => {
    onUpdate({ itemName: null, price: null });
    setIsDropdownOpen(false);
    setSearch('');
  };

  return (
    <div className="flex items-center gap-2 bg-gray-800/50 rounded px-2 py-1.5">
      {/* Slot number */}
      <span className="text-[10px] text-gray-600 w-3 text-center flex-shrink-0">{index + 1}</span>

      {/* Item selector */}
      <div className="relative flex-1" ref={dropdownRef}>
        {selectedItem ? (
          <button
            className="flex items-center gap-1.5 w-full text-left text-[11px] text-gray-200 hover:text-white transition-colors"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <img src={selectedItem.img} alt={selectedItem.name} className="w-5 h-5 object-contain" />
            <span className="truncate">{selectedItem.name}</span>
            <button
              className="ml-auto text-gray-600 hover:text-red-400 flex-shrink-0"
              onClick={(e) => { e.stopPropagation(); clearItem(); }}
            >
              <X size={10} />
            </button>
          </button>
        ) : (
          <button
            className="w-full text-left text-[11px] text-gray-600 italic hover:text-gray-400 transition-colors"
            onClick={() => { setIsDropdownOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
          >
            Select item...
          </button>
        )}

        {isDropdownOpen && (
          <div className="absolute top-full left-0 mt-1 w-56 bg-gray-900 border border-gray-600 rounded-lg shadow-xl overflow-hidden" style={{ zIndex: 10001 }}>
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="w-full px-2 py-1.5 text-[11px] bg-gray-800 border-b border-gray-700 text-gray-200 placeholder-gray-600 outline-none"
              autoFocus
            />
            <div className="max-h-48 overflow-y-auto">
              {filteredItems.map((item) => (
                <button
                  key={item.name}
                  className="w-full text-left px-2 py-1 text-[11px] text-gray-300 hover:bg-gray-800 flex items-center gap-1.5"
                  onClick={() => selectItem(item)}
                >
                  <img src={item.img} alt={item.name} className="w-4 h-4 object-contain" />
                  {item.name}
                </button>
              ))}
              {filteredItems.length === 0 && (
                <div className="px-2 py-2 text-[10px] text-gray-600 text-center">No items found</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Price input */}
      <div className="flex items-center gap-1 flex-shrink-0">
        <img src={RupeeIcon} alt="rupee" className="w-3.5 h-3.5 object-contain" />
        <input
          type="number"
          min={0}
          max={999}
          value={slot.price ?? ''}
          onChange={(e) => onUpdate({ price: e.target.value ? parseInt(e.target.value, 10) : null })}
          placeholder="—"
          className="w-12 px-1 py-0.5 text-[11px] bg-gray-800 border border-gray-700 rounded text-gray-200 text-center outline-none focus:border-zelda-gold/50"
        />
      </div>
    </div>
  );
}

/** Count filled shop items for badge display */
export function getShopItemCount(items: ShopItem[] | undefined): number {
  if (!items) return 0;
  return items.filter(s => s.itemName !== null).length;
}
